import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { PencilIcon } from 'lucide-react';

import Dialog from 'src/components/dialog/Dialog';
import { useUpdateSmartFields, type SmartFieldsPayload } from 'src/services/api/leads/use-update-smart-fields';

import styles from 'src/components/lead-sidebar/EditFactButton.module.css';

export type EditableField = {
    /** Smart-field key written to the backend (e.g. "phone_number", "monthly_income"). */
    key: string;
    /** Sub-label for the input; omit for a single-value fact. */
    label?: string;
    value: string;
};

type Props = {
    leadUuid: string;
    /** Modal title, rendered as "Edit {title}". */
    title: string;
    /** One entry for a normal fact, several for a multi-value fact like income. */
    fields: EditableField[];
    /** Disambiguates this editor's test ids / trigger (e.g. "phone_number", "income"). */
    testId: string;
    /** Optional clickable trigger element; defaults to a pencil icon button. */
    children?: React.ReactNode;
};

const initialValues = (fields: EditableField[]): Record<string, string> =>
    Object.fromEntries(fields.map((field) => [field.key, field.value]));

/** Pencil button to edit a lead fact — one value, or several like income's monthly/annual — saving the changed fields via a whole-blob PUT. */
export default function EditFactButton({ leadUuid, title, fields, testId, children }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [values, setValues] = useState<Record<string, string>>(() => initialValues(fields));
    const { mutate, isLoading } = useUpdateSmartFields(leadUuid);

    const open = () => {
        setValues(initialValues(fields));
        setIsOpen(true);
    };
    const close = () => setIsOpen(false);

    const save = () => {
        /* Send only the changed keys; the hook merges them over the freshest cached blob. */
        const changed: SmartFieldsPayload = {};
        fields.forEach((field) => {
            if (values[field.key] !== field.value) {
                changed[field.key] = values[field.key];
            }
        });
        mutate(changed, { onSuccess: close });
    };

    return (
        <>
            {children ? (
                <button type="button" onClick={open} className={styles.textTrigger} data-testid={`edit-fact-${testId}`}>
                    {children}
                </button>
            ) : (
                <IconButton
                    size="small"
                    onClick={open}
                    aria-label={`Edit ${title}`}
                    data-testid={`edit-fact-${testId}`}
                    className={styles.editButton}
                >
                    <PencilIcon size={13} />
                </IconButton>
            )}

            <Dialog open={isOpen} onClose={close}>
                <DialogTitle>Edit {title}</DialogTitle>
                <DialogContent className={styles.content}>
                    {fields.map((field, index) => (
                        <TextField
                            key={field.key}
                            autoFocus={index === 0}
                            fullWidth
                            size="small"
                            margin="dense"
                            label={field.label}
                            value={values[field.key]}
                            onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                            data-testid={`edit-fact-input-${field.key}`}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="dark" onClick={close} data-testid={`edit-fact-cancel-${testId}`}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={save}
                        disabled={isLoading}
                        data-testid={`edit-fact-save-${testId}`}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
